// Página de gestión de comunidades privadas
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users, Plus, Copy, Crown, Check, UserPlus,
} from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import Modal from '../components/ui/Modal'
import { useToast } from '../components/ui/Toast'
import { useCommunities } from '../hooks/useCommunities'
import useAuthStore from '../store/useAuthStore'

// Componente para renderizar la tarjeta de cada comunidad con sus datos en tiempo real
function CommunityCard({ community, copyCode, copiedCode, useCommunity, currentUser }) {
  const { data: rankingData, isLoading } = useCommunity(community.id)
  const ranking = rankingData?.datos?.ranking || []
  
  // Encontrar posición del usuario actual
  const myRankItem = ranking.find(m => Number(m.usuario_id) === Number(currentUser?.id))
  const myRank = myRankItem ? myRankItem.posicion_liga : '-'
  
  // Obtener los primeros 3 puestos
  const topMembers = ranking.slice(0, 3)
  const isOwner = Number(community.creador_id) === Number(currentUser?.id)

  return (
    <Card variant="default" padding="none" hover={false}>
      {/* Encabezado de la comunidad */}
      <div className="p-5 border-b border-border-subtle">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-cyan/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-cyan" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display font-bold text-text-primary">{community.nombre}</h3>
                {isOwner && (
                  <Badge variant="warning" size="sm" icon={Crown}>Creador</Badge>
                )}
              </div>
              <p className="text-xs text-text-muted">
                {community.total_miembros} miembros · Código: {community.codigo}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => copyCode(community.codigo)}
              className="p-2 rounded-lg text-text-muted hover:text-cyan hover:bg-cyan/10 transition-colors"
              title="Copiar código de invitación"
            >
              {copiedCode === community.codigo ? (
                <Check className="w-4 h-4 text-success" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tu posición en la comunidad */}
      <div className="px-5 py-3 bg-cyan/5 border-b border-border-subtle">
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-secondary">Tu posición</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg font-bold text-cyan">#{myRank}</span>
            {myRank === 1 && <Crown className="w-4 h-4 text-warning" />}
          </div>
        </div>
      </div>

      {/* Top 3 de la comunidad */}
      <div className="p-5">
        <p className="text-xs text-text-muted uppercase tracking-wider font-semibold mb-3">
          Primeros puestos
        </p>
        {isLoading ? (
          <div className="text-center py-2 text-xs text-text-muted">
            Cargando clasificación...
          </div>
        ) : (
          <div className="space-y-2">
            {topMembers.map((member) => {
              const isMe = Number(member.usuario_id) === Number(currentUser?.id)
              const nombreCompleto = `${member.nombre} ${member.apellido}`
              return (
                <div
                  key={member.usuario_id}
                  className={`
                    flex items-center gap-3 px-3 py-2 rounded-lg
                    ${isMe ? 'bg-cyan/5 border border-cyan/10' : 'bg-surface-card'}
                  `}
                >
                  <span className={`font-mono text-xs font-bold w-5 text-center ${
                    member.posicion_liga === 1 ? 'text-warning' :
                    member.posicion_liga === 2 ? 'text-text-secondary' :
                    'text-text-muted'
                  }`}>
                    {member.posicion_liga}
                  </span>
                  <span className={`flex-1 text-sm ${isMe ? 'font-semibold text-cyan' : 'text-text-primary'}`}>
                    {isMe ? 'Tú' : nombreCompleto}
                  </span>
                  <span className="font-mono text-sm font-semibold text-text-secondary">
                    {member.puntos} pts
                  </span>
                </div>
              )
            })}
            {topMembers.length === 0 && (
              <p className="text-xs text-text-muted">Aún no hay miembros en esta comunidad.</p>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

export default function Communities() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showJoinModal, setShowJoinModal] = useState(false)
  const [copiedCode, setCopiedCode] = useState(null)
  const [newCommunityName, setNewCommunityName] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const toast = useToast()
  
  const { user } = useAuthStore()
  const { communities, isLoadingCommunities, useCommunity, createCommunity, joinCommunity } = useCommunities()

  // Copiar código de invitación al portapapeles
  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      toast.success('Código copiado al portapapeles')
      setTimeout(() => setCopiedCode(null), 2000)
    } catch {
      toast.error('No se pudo copiar el código')
    }
  }

  // Crear nueva comunidad
  const handleCreateCommunity = async () => {
    if (!newCommunityName.trim()) {
      toast.warning('Escribe un nombre para la comunidad')
      return
    }
    try {
      await createCommunity.mutateAsync({ nombre: newCommunityName })
      toast.success(`Comunidad "${newCommunityName}" creada exitosamente`, '¡Comunidad creada!')
      setShowCreateModal(false)
      setNewCommunityName('')
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al crear la comunidad')
    }
  }

  // Unirse a una comunidad
  const handleJoinCommunity = async () => {
    if (!joinCode.trim()) {
      toast.warning('Ingresa el código de invitación')
      return
    }
    try {
      await joinCommunity.mutateAsync(joinCode)
      toast.success('Te has unido a la comunidad', '¡Bienvenido!')
      setShowJoinModal(false)
      setJoinCode('')
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al unirse a la comunidad')
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Encabezado */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary flex items-center gap-3">
              <Users className="w-7 h-7 text-cyan" />
              Mis Comunidades
            </h1>
            <p className="text-text-secondary mt-1">
              Compite con amigos en comunidades privadas.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" icon={UserPlus} onClick={() => setShowJoinModal(true)}>
              Unirme
            </Button>
            <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
              Crear comunidad
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Lista de comunidades */}
      <div className="space-y-4">
        {isLoadingCommunities ? (
          <div className="text-center py-12 text-text-muted">Cargando tus comunidades...</div>
        ) : (
          <>
            <AnimatePresence>
              {communities.map((community, i) => (
                <motion.div
                  key={community.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <CommunityCard
                    community={community}
                    copyCode={copyCode}
                    copiedCode={copiedCode}
                    useCommunity={useCommunity}
                    currentUser={user}
                  />
                </motion.div>
              ))}
            </AnimatePresence>

            {communities.length === 0 && (
              <div className="text-center py-16 bg-surface-card/30 border border-border-subtle rounded-3xl backdrop-blur-md">
                <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-display font-semibold text-text-primary mb-2">
                  Aún no estás en ninguna comunidad
                </h3>
                <p className="text-sm text-text-secondary mb-6 max-w-sm mx-auto">
                  Crea tu propia comunidad o únete a una existente con un código de invitación.
                </p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" icon={UserPlus} onClick={() => setShowJoinModal(true)}>
                    Unirme con código
                  </Button>
                  <Button icon={Plus} onClick={() => setShowCreateModal(true)}>
                    Crear comunidad
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal para crear comunidad */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Crear nueva comunidad"
      >
        <div className="space-y-4">
          <Input
            label="Nombre de la comunidad"
            placeholder="Ej: Amigos del barrio"
            value={newCommunityName}
            onChange={(e) => setNewCommunityName(e.target.value)}
          />
          <p className="text-xs text-text-muted">
            Se generará un código de invitación que podrás compartir con tus amigos.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button icon={Plus} onClick={handleCreateCommunity}>
              Crear comunidad
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para unirse a comunidad */}
      <Modal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        title="Unirse a una comunidad"
      >
        <div className="space-y-4">
          <Input
            label="Código de invitación"
            placeholder="Ej: AMG-2026"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
          />
          <p className="text-xs text-text-muted">
            Pide el código de invitación al creador de la comunidad.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={() => setShowJoinModal(true)}>
              Cancelar
            </Button>
            <Button icon={UserPlus} onClick={handleJoinCommunity}>
              Unirme
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
